import React, { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const AUTOPLAY_DELAY = 5000;

/**
 * Carrousel léger (embla-carousel, déjà une dépendance du projet — pas de
 * grosse librairie ajoutée) : défilement tactile natif, flèches + points de
 * navigation, pause au survol / focus, respect de prefers-reduced-motion,
 * images en lazy-loading.
 *
 * items: [{ id, src, alt }]
 */
const SimpleCarousel = ({ items, className = "", imageClassName = "" }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: items.length > 1 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const autoplayRef = useRef(null);

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Autoplay — désactivé si prefers-reduced-motion, en pause au survol/focus,
  // et si un seul élément (rien à faire défiler).
  useEffect(() => {
    if (!emblaApi || prefersReducedMotion || items.length <= 1) return undefined;
    if (isHovering) return undefined;

    autoplayRef.current = setInterval(() => {
      emblaApi.scrollNext();
    }, AUTOPLAY_DELAY);

    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [emblaApi, isHovering, prefersReducedMotion, items.length]);

  if (!items || items.length === 0) return null;

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onFocus={() => setIsHovering(true)}
      onBlur={() => setIsHovering(false)}
    >
      <div className="overflow-hidden rounded-2xl" ref={emblaRef}>
        <div className="flex">
          {items.map((item) => (
            <div className="relative flex-[0_0_100%] min-w-0" key={item.id}>
              <img
                src={item.src}
                alt={item.alt}
                loading="lazy"
                className={`w-full h-64 md:h-96 object-cover ${imageClassName}`}
              />
            </div>
          ))}
        </div>
      </div>

      {items.length > 1 && (
        <>
          <button
            type="button"
            onClick={scrollPrev}
            aria-label="Image précédente"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#FDFBF5]/90 text-[#21167D] shadow-md flex items-center justify-center hover:bg-[#FDFBF5] transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={scrollNext}
            aria-label="Image suivante"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#FDFBF5]/90 text-[#21167D] shadow-md flex items-center justify-center hover:bg-[#FDFBF5] transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="flex justify-center gap-2 mt-4">
            {items.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollTo(index)}
                aria-label={`Aller à l'image ${index + 1}`}
                aria-current={index === selectedIndex}
                className={`h-2.5 rounded-full transition-all ${
                  index === selectedIndex ? "w-6 bg-[#21167D]" : "w-2.5 bg-[#DCB071]"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default SimpleCarousel;
